import {
  ICreateFlashcard,
  IGenerateFlashcardWithAI,
  IMoveFlashcard,
  IUpdateFlashcard
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { useFlashcardForm } from './useFlashcardForm';

import { sdk } from '@/services';

const FlashCardService = sdk.getService('FlashcardService');

export const useMutateFlashcard = (collectionId: string) => {
  const { message } = App.useApp();
  const { t } = useTranslation('FlashcardsCommonTranslations');
  const { setIsOpenGeneratingFlashcardWithAI } = useFlashcardForm();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['flashcards']
    });
  };

  const { mutate: createFlashcard, isPending: isCreating } = useMutation({
    mutationFn: (flashcardData: ICreateFlashcard) =>
      FlashCardService.createFlashCard(flashcardData, collectionId),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_CREATED_SUCCESSFULLY'));
    }
  });

  const { mutate: generateFlashcardWithAI, isPending: isGeneratingFlashcard } = useMutation({
    mutationFn: ({ flashcardCount, sections }: IGenerateFlashcardWithAI) =>
      FlashCardService.generateFlashcardWithAI(collectionId, { flashcardCount, sections }),
    onSuccess: () => {
      onSuccess();
      setIsOpenGeneratingFlashcardWithAI(false);
      message.success(t('FLASHCARD_CREATED_SUCCESSFULLY'));
    },
    onError: () => {
      setIsOpenGeneratingFlashcardWithAI(false);
      message.error(t('ERROR_GENERATING_FLASHCARD'));
    }
  });

  const { mutate: updateFlashcard, isPending: isUpdating } = useMutation({
    mutationFn: (flashcard: IUpdateFlashcard) => FlashCardService.updateFlashCard(flashcard),
    onSuccess: () => {
      onSuccess();
    }
  });

  const { mutate: removeFlashcard, isPending: isRemoving } = useMutation({
    mutationFn: (flashcardId: string) =>
      FlashCardService.removeFlashCard(flashcardId, collectionId),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_REMOVED_SUCCESSFULLY'));
    }
  });

  const { mutate: moveFlashcardToCollection, isPending: isMoving } = useMutation({
    mutationFn: (moveFlashcard: IMoveFlashcard) =>
      FlashCardService.moveFlashcardToCollection(moveFlashcard, collectionId),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_MOVED_SUCCESSFULLY'));
    }
  });

  return {
    isWorking: isCreating || isUpdating || isRemoving || isMoving || isGeneratingFlashcard,
    createFlashcard,
    updateFlashcard,
    removeFlashcard,
    moveFlashcardToCollection,
    generateFlashcardWithAI
  };
};
